import { useNavigate } from "react-router-dom";
import { Plane, CarFront, Briefcase, CalendarHeart, Users, PartyPopper, Route, ArrowRight, Phone } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { PHONE_TEL, waLink } from "../../lib/site";
import { useLang } from "../../lib/i18n";

const AIRPORTS = [
    "Eindhoven Airport",
    "Amsterdam Schiphol",
    "Rotterdam The Hague Airport",
    "Brussels Airport",
    "Düsseldorf Airport",
    "Weeze Airport",
    "Maastricht Aachen Airport",
    "Köln Bonn Airport",
];

const SERVICE_CONFIG = [
    { icon: Plane, rit: "airport", featured: true },
    { icon: CarFront, href: "tel" },
    { icon: Briefcase, rit: "business" },
    { icon: CalendarHeart, rit: "daily" },
    { icon: Users, rit: "group" },
    { icon: PartyPopper, rit: "event" },
    { icon: Route, href: "waLong" },
];

const ServiceCard = ({ config, text, index, longWaLink, onRit }) => {
    const href = config.href === "tel" ? PHONE_TEL : config.href === "waLong" ? longWaLink : null;
    const external = config.href === "waLong";
    const CtaIcon = config.href === "tel" ? Phone : ArrowRight;
    const inner = (
        <>
            <div className="flex items-start justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-400/10 text-amber-400">
                    <config.icon size={24} />
                </span>
                <span className="font-mono text-xs text-slate-600">0{index + 1}</span>
            </div>
            <h3 className="mt-6 font-display font-wide text-xl font-bold uppercase tracking-tight text-white sm:text-2xl">
                {text.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{text.text}</p>
            {config.featured && (
                <div className="mt-5 flex flex-wrap gap-2" data-testid="airport-list">
                    {AIRPORTS.map((airport) => (
                        <span key={airport} className="chip">{airport}</span>
                    ))}
                </div>
            )}
            <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-amber-400 transition-[gap] duration-300 group-hover:gap-3.5">
                {text.cta} <CtaIcon size={16} />
            </span>
        </>
    );

    const cls = `card-lift group flex flex-col rounded-2xl border border-white/8 bg-night-850/80 p-7 sm:p-8 ${
        config.featured ? "sm:col-span-2 lg:col-span-2" : ""
    }`;

    return (
        <Reveal delay={0.05 * (index % 3)} className={config.featured ? "sm:col-span-2 lg:col-span-2" : ""}>
            {href ? (
                <a
                    href={href}
                    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    data-testid={`service-card-${index}`}
                    className={cls}
                >
                    {inner}
                </a>
            ) : (
                <button onClick={() => onRit(config.rit)} data-testid={`service-card-${index}`} className={`${cls} w-full text-left`}>
                    {inner}
                </button>
            )}
        </Reveal>
    );
};

export const Services = () => {
    const { t } = useLang();
    const navigate = useNavigate();
    const longWaLink = waLink(t.services.longWa);
    const onRit = (rit) => navigate(`/boeken?rit=${rit}`);
    return (
        <section id="diensten" data-testid="services-section" className="relative py-24 sm:py-32">
            <div className="container-x">
                <SectionHeading
                    num="01"
                    eyebrow={t.services.eyebrow}
                    title={t.services.title}
                    text={t.services.text}
                />
                <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {SERVICE_CONFIG.map((config, i) => (
                        <ServiceCard key={i} config={config} text={t.services.items[i]} index={i} longWaLink={longWaLink} onRit={onRit} />
                    ))}
                </div>
            </div>
        </section>
    );
};
